import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Package, Upload, Loader2, Landmark, Check } from "lucide-react";
import { api, errMsg, fmt, fmtDate, PAY_METHOD, PAY_STATUS, STATUS } from "../lib/api";
import { Empty, PageTitle, Spinner, StatusBadge, btnGhost, btnPrimary } from "../components/ui";
import ReceiptLink from "../components/ReceiptLink";
import WhatsAppButton from "../components/WhatsAppButton";
import TrackingPanel from "../map/TrackingPanel";
import ReturnRequest from "../components/ReturnRequest";

export function Orders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders/mine").then(({ data }) => setOrders(data)); }, []);
  if (!orders) return <Spinner />;
  if (!orders.length) return <Empty icon={Package} title="Ainda não tem encomendas" action={<Link to="/catalogo" className={btnPrimary}>Começar a comprar</Link>} />;
  return (
    <div data-testid="orders-page">
      <PageTitle title="Meus Pedidos" />
      <div className="flex flex-col gap-3">
        {orders.map((o) => (
          <Link key={o.id} to={`/conta/pedidos/${o.id}`} className="bo-card flex flex-wrap items-center gap-4 p-4 hover:ring-2 hover:ring-bo-green/30" data-testid={`order-row-${o.number}`}>
            <div className="flex -space-x-3">{o.items.slice(0, 3).map((i) => <img key={i.offer_id} src={i.image} alt="" className="h-11 w-11 rounded-full border-2 border-white object-cover" />)}</div>
            <div className="min-w-0 flex-1">
              <p className="font-bold">{o.number}</p>
              <p className="text-xs text-gray-500">{fmtDate(o.created_at)} · {o.items.length} artigos · {PAY_METHOD[o.payment_method]}</p>
            </div>
            <span className="text-xs text-gray-500">{PAY_STATUS[o.payment_status]}</span>
            <StatusBadge status={o.status} />
            <p className="w-28 text-right font-bold">{fmt(o.total)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

const FLOW = ["pending_payment", "confirmed", "preparing", "ready", "out_for_delivery", "delivered"];

function Timeline({ status, cod }) {
  const flow = cod ? FLOW.slice(1) : FLOW;
  const idx = flow.indexOf(status);
  return (
    <div className="flex flex-wrap gap-2" data-testid="order-timeline">
      {flow.map((s, i) => (
        <span key={s} className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${i <= idx ? "bg-bo-green text-white" : "bg-gray-100 text-gray-400"}`}>
          {i < idx && <Check className="h-3 w-3" />}{STATUS[s].label}
        </span>
      ))}
    </div>
  );
}

function PaymentBox({ order, reload }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const canUpload = order.payment_method === "transfer" && order.status === "pending_payment" && ["pending", "rejected"].includes(order.payment_status);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Ficheiro demasiado grande (máximo 5 MB)");
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try { await api.post(`/orders/${order.id}/receipt`, form); toast.success("Comprovativo enviado para validação"); reload(); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <section className="bo-card p-5" data-testid="payment-box">
      <h2 className="mb-1 font-bold">Pagamento · {PAY_METHOD[order.payment_method]}</h2>
      <p className="mb-3 text-sm text-gray-600" data-testid="payment-status">{PAY_STATUS[order.payment_status]}</p>
      {order.payment_method === "transfer" && order.status === "pending_payment" && (
        <div className="mb-4 rounded-2xl bg-bo-bg p-4 text-sm">
          <p className="mb-2 font-semibold">Transfira {fmt(order.total)} para:</p>
          {(order.bank_accounts || []).map((b) => (
            <div key={b.iban} className="mb-2 flex gap-3" data-testid="bank-account-info">
              <Landmark className="mt-0.5 h-4 w-4 text-bo-green" />
              <div><p className="font-semibold">{b.bank_name}</p><p>Titular: {b.holder}</p><p className="font-mono">{b.iban}</p></div>
            </div>
          ))}
          {!order.bank_accounts?.length && <p className="text-amber-700">Dados bancários a configurar. Contacte o apoio ao cliente.</p>}
          <p className="mt-2 text-xs text-gray-500">Indique a referência <strong>{order.number}</strong> no descritivo. A encomenda só é preparada após a validação manual do pagamento.</p>
        </div>
      )}
      {canUpload && (
        <>
          <input ref={fileRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="receipt-file-input" />
          <button onClick={() => fileRef.current.click()} disabled={busy} className={btnPrimary} data-testid="upload-receipt-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Enviar comprovativo
          </button>
          <p className="mt-2 text-xs text-gray-500">PDF, JPG, PNG ou WEBP · máximo 5 MB · visível apenas para si e para a administração.</p>
        </>
      )}
      {order.receipts?.length > 0 && <div className="mt-4 flex flex-col gap-2">{order.receipts.map((r) => <ReceiptLink key={r.id} r={r} />)}</div>}
    </section>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const load = () => api.get(`/orders/${id}`).then(({ data }) => setO(data)).catch(() => setO(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (o === null) return <Spinner />;
  if (!o) return <Empty icon={Package} title="Encomenda não encontrada" />;
  const cancellable = ["pending_payment", "confirmed"].includes(o.status) && o.payment_status !== "confirmed"
    && Object.values(o.store_fulfillment).every((v) => v === "pending");
  const cancel = async () => {
    if (!window.confirm("Cancelar esta encomenda?")) return;
    try { await api.post(`/orders/${o.id}/cancel`); toast.success("Encomenda cancelada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-5" data-testid="order-detail-page">
      <PageTitle title={`Encomenda ${o.number}`} sub={`Criada em ${fmtDate(o.created_at)}`}
        right={<div className="flex flex-wrap items-center gap-2"><WhatsAppButton reference={o.number} /><Link to={`/conta/reclamacoes?tipo=order&ref=${o.number}`} className={btnGhost} data-testid="order-complaint-link">Reclamar</Link><StatusBadge status={o.status} />{cancellable && <button onClick={cancel} className={btnGhost} data-testid="cancel-order-btn">Cancelar</button>}</div>} />
      {!["cancelled", "delivery_failed"].includes(o.status) && <Timeline status={o.status} cod={o.payment_method === "cash_on_delivery"} />}
      {o.delivery_code && !["delivered", "cancelled", "delivery_failed"].includes(o.status) && (
        <p className="bo-card flex flex-wrap items-center gap-3 p-4 text-sm" data-testid="delivery-code-box">
          Código de entrega: <strong className="rounded-lg bg-bo-yellow px-3 py-1 font-mono text-lg tracking-widest" data-testid="delivery-code">{o.delivery_code}</strong>
          <span className="text-gray-500">Dê este código ao estafeta apenas quando receber a encomenda.</span>
        </p>
      )}
      <TrackingPanel order={o} />
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <section className="bo-card p-5">
          <h2 className="mb-3 font-bold">Artigos</h2>
          <div className="divide-y divide-gray-100">
            {o.items.map((i) => (
              <div key={i.offer_id} className="flex items-center gap-3 py-3">
                <img src={i.image} alt="" className="h-12 w-12 rounded-lg object-cover" />
                <div className="flex-1"><p className="font-medium">{i.name}</p><p className="text-xs text-gray-500">{i.store_name} · {i.qty} × {fmt(i.unit_price)}</p></div>
                <p className="font-semibold">{fmt(i.line_total)}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm">
            <p className="flex justify-between"><span>Subtotal</span><span>{fmt(o.subtotal)}</span></p>
            <p className="flex justify-between"><span>Entrega ({o.delivery_method === "pickup" ? "levantamento" : "domicílio"})</span><span>{fmt(o.delivery_fee)}</span></p>
            <p className="flex justify-between text-base font-bold"><span>Total</span><span data-testid="order-total">{fmt(o.total)}</span></p>
          </div>
          <div className="mt-4 rounded-xl bg-bo-bg p-3 text-sm">
            <p className="font-semibold">{o.address.full_name} · {o.address.phone}</p>
            <p className="text-gray-600">{o.address.address_line}, {o.address.district}</p>
            {o.address.landmark && <p className="text-xs text-gray-500">Referência: {o.address.landmark}</p>}
          </div>
        </section>
        <div className="flex flex-col gap-5">
          <PaymentBox order={o} reload={load} />
          <ReturnRequest order={o} />
          <section className="bo-card p-5">
            <h2 className="mb-3 font-bold">Histórico</h2>
            <ol className="flex flex-col gap-2 text-sm">
              {o.history.slice().reverse().map((h, i) => <li key={i} className="border-l-2 border-bo-green/40 pl-3"><p className="font-medium">{h.note || STATUS[h.status]?.label}</p><p className="text-xs text-gray-500">{fmtDate(h.at)}</p></li>)}
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
